<script setup>
import { useAlert } from 'dashboard/composables';
import AddCannedAudio from './AddCannedAudio.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, onMounted, ref, defineOptions } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';

import Button from 'dashboard/components-next/button/Button.vue';
import {
    BaseTable,
    BaseTableRow,
    BaseTableCell,
} from 'dashboard/components-next/table';

defineOptions({
    name: 'CannedAudioSettings',
});

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showAddPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const activeResponse = ref({});
const loading = ref({});

const searchQuery = ref('');
const activeTab = ref('all');
const currentPage = ref(1);
const perPage = ref(15);

const records = computed(() => getters.getCannedAudioResponses.value);

const filteredByTab = computed(() => {
    const all = records.value;
    if (activeTab.value === 'global')
        return all.filter(r => r.scope === 'global');
    if (activeTab.value === 'personal')
        return all.filter(r => r.scope === 'personal');
    return all;
});

const filteredRecords = computed(() => {
    const query = searchQuery.value.trim();
    if (!query) return filteredByTab.value;
    return picoSearch(filteredByTab.value, query, ['short_code', 'subject']);
});

const totalRecords = computed(() => filteredRecords.value.length);
const totalPages = computed(() =>
    Math.max(1, Math.ceil(totalRecords.value / perPage.value))
);

const paginatedRecords = computed(() => {
    const start = (currentPage.value - 1) * perPage.value;
    return filteredRecords.value.slice(start, start + perPage.value);
});

const uiFlags = computed(() => getters.getCannedAudioUIFlags.value);

const deleteConfirmText = computed(
    () =>
        `${t('CANNED_AUDIO_MGMT.DELETE.CONFIRM.YES')} ${activeResponse.value.short_code}`
);

const deleteRejectText = computed(
    () =>
        `${t('CANNED_AUDIO_MGMT.DELETE.CONFIRM.NO')} ${activeResponse.value.short_code}`
);

const deleteMessage = computed(() => {
    return ` ${activeResponse.value.short_code} ? `;
});

const fetchCannedAudios = async () => {
    try {
        await store.dispatch('getCannedAudioResponses');
    } catch (error) {
        // Ignore Error
    }
};

onMounted(() => {
    fetchCannedAudios();
});

const showAlertMessage = message => {
    loading.value[activeResponse.value.id] = false;
    activeResponse.value = {};
    useAlert(message);
};

const openAddPopup = () => {
    showAddPopup.value = true;
};
const hideAddPopup = () => {
    showAddPopup.value = false;
};

const openDeletePopup = response => {
    showDeleteConfirmationPopup.value = true;
    activeResponse.value = response;
};

const closeDeletePopup = () => {
    showDeleteConfirmationPopup.value = false;
};

const deleteCannedAudio = async id => {
    try {
        await store.dispatch('deleteCannedAudioResponse', id);
        showAlertMessage(t('CANNED_AUDIO_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    } catch (error) {
        const errorMessage =
            error?.message || t('CANNED_AUDIO_MGMT.DELETE.API.ERROR_MESSAGE');
        showAlertMessage(errorMessage);
    }
};

const confirmDeletion = () => {
    loading.value[activeResponse.value.id] = true;
    closeDeletePopup();
    deleteCannedAudio(activeResponse.value.id);
};

const tableHeaders = computed(() => {
    return [
        t('CANNED_AUDIO_MGMT.LIST.TABLE_HEADER.SHORT_CODE'),
        t('CANNED_AUDIO_MGMT.LIST.TABLE_HEADER.ACTIONS'),
    ];
});

const tabs = computed(() => [
    { key: 'all', label: t('CANNED_AUDIO_MGMT.TABS.ALL') },
    { key: 'global', label: t('CANNED_AUDIO_MGMT.TABS.GLOBAL') },
    { key: 'personal', label: t('CANNED_AUDIO_MGMT.TABS.PERSONAL') },
]);

const onTabChange = key => {
    activeTab.value = key;
    currentPage.value = 1;
};

const onSearchChange = () => {
    currentPage.value = 1;
};

const goToPage = page => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
    }
};

const visiblePages = computed(() => {
    const pages = [];
    const total = totalPages.value;
    const current = currentPage.value;
    const maxVisible = 5;

    if (total <= maxVisible) {
        for (let i = 1; i <= total; i++) pages.push(i);
    } else {
        pages.push(1);
        let start = Math.max(2, current - 1);
        let end = Math.min(total - 1, current + 1);

        if (current <= 3) {
            end = Math.min(4, total - 1);
        }
        if (current >= total - 2) {
            start = Math.max(total - 3, 2);
        }

        if (start > 2) pages.push('...');
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < total - 1) pages.push('...');
        pages.push(total);
    }
    return pages;
});
</script>

<template>
    <SettingsLayout :is-loading="uiFlags.fetchingList" :loading-message="$t('CANNED_AUDIO_MGMT.LOADING')"
        :no-records-found="!records.length" :no-records-message="$t('CANNED_AUDIO_MGMT.LIST.404')">
        <template #header>
            <BaseSettingsHeader v-model:search-query="searchQuery" :title="$t('CANNED_AUDIO_MGMT.HEADER')"
                :description="$t('CANNED_AUDIO_MGMT.DESCRIPTION')"
                :search-placeholder="$t('CANNED_AUDIO_MGMT.SEARCH_PLACEHOLDER')" feature-name="canned_responses"
                @update:search-query="onSearchChange">
                <template #actions>
                    <Button :label="$t('CANNED_AUDIO_MGMT.HEADER_BTN_TXT')" size="sm" @click="openAddPopup" />
                </template>
            </BaseSettingsHeader>
        </template>

        <template #body>
            <div class="flex gap-1 mb-4 border-b border-n-weak">
                <button v-for="tab in tabs" :key="tab.key"
                    class="px-4 py-2 text-sm font-medium transition-colors cursor-pointer rounded-t-lg" :class="{
                        'text-n-brand bg-n-alpha-black2 border-b-2 border-n-brand':
                            activeTab === tab.key,
                        'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-black1':
                            activeTab !== tab.key,
                    }" @click="onTabChange(tab.key)">
                    {{ tab.label }}
                    <span v-if="tab.key === 'all'" class="ml-1 text-xs text-n-slate-10">
                        ({{ records.length }})
                    </span>
                </button>
            </div>

            <BaseTable :headers="tableHeaders" :items="paginatedRecords" :no-data-message="!records.length
                    ? $t('CANNED_AUDIO_MGMT.LIST.404')
                    : searchQuery
                        ? $t('CANNED_AUDIO_MGMT.NO_RESULTS')
                        : ''
                ">
                <template #header-0>
                    {{ tableHeaders[0] }}
                </template>
                <template #header-1>
                    {{ tableHeaders[1] }}
                </template>

                <template #row="{ items }">
                    <BaseTableRow v-for="audioItem in items" :key="audioItem.id" :item="audioItem">
                        <template #default>
                            <BaseTableCell class="max-w-0">
                                <div class="flex flex-col gap-2 min-w-0">
                                    <div class="flex items-center gap-2">
                                        <span class="text-heading-3 text-n-slate-12 truncate">
                                            {{ audioItem.short_code }}
                                        </span>
                                        <span
                                            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
                                            :class="{
                                                'bg-n-teal-2 text-n-teal-11':
                                                    audioItem.scope === 'global',
                                                'bg-n-violet-2 text-n-violet-11':
                                                    audioItem.scope === 'personal',
                                            }">
                                            {{
                                                audioItem.scope === 'global'
                                                    ? $t('CANNED_AUDIO_MGMT.BADGE.GLOBAL')
                                                    : $t('CANNED_AUDIO_MGMT.BADGE.PERSONAL')
                                            }}
                                        </span>
                                    </div>
                                    <p class="text-body-main text-n-slate-11 mb-1">
                                        {{ audioItem.subject }}
                                    </p>
                                    <audio v-if="audioItem.audio_url" controls preload="none"
                                        class="w-full max-w-xs h-8">
                                        <source :src="audioItem.audio_url" />
                                    </audio>
                                </div>
                            </BaseTableCell>

                            <BaseTableCell align="end" class="w-24">
                                <div class="flex gap-3 justify-end flex-shrink-0">
                                    <Button v-tooltip.top="$t('CANNED_AUDIO_MGMT.DELETE.BUTTON_TEXT')" icon="i-woot-bin"
                                        slate sm class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                                        :is-loading="loading[audioItem.id]" @click="openDeletePopup(audioItem)" />
                                </div>
                            </BaseTableCell>
                        </template>
                    </BaseTableRow>
                </template>
            </BaseTable>

            <div v-if="totalRecords > perPage" class="flex items-center justify-between mt-4 px-2">
                <p class="text-sm text-n-slate-11">
                    {{ (currentPage - 1) * perPage + 1 }}–{{
                        Math.min(currentPage * perPage, totalRecords)
                    }}
                    {{ $t('CANNED_AUDIO_MGMT.PAGINATION.OF') }} {{ totalRecords }}
                </p>

                <nav class="flex items-center gap-1">
                    <button
                        class="p-1.5 rounded-lg text-sm text-n-slate-11 hover:bg-n-alpha-black2 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                        :disabled="currentPage === 1" @click="goToPage(currentPage - 1)">
                        ‹
                    </button>

                    <template v-for="page in visiblePages" :key="page">
                        <span v-if="page === '...'" class="px-2 text-sm text-n-slate-10">
                            …
                        </span>
                        <button v-else
                            class="min-w-[2rem] h-8 rounded-lg text-sm font-medium transition-colors cursor-pointer"
                            :class="{
                                'bg-n-brand text-white': page === currentPage,
                                'text-n-slate-11 hover:bg-n-alpha-black2':
                                    page !== currentPage,
                            }" @click="goToPage(page)">
                            {{ page }}
                        </button>
                    </template>

                    <button
                        class="p-1.5 rounded-lg text-sm text-n-slate-11 hover:bg-n-alpha-black2 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                        :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)">
                        ›
                    </button>
                </nav>
            </div>
        </template>

        <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
            <AddCannedAudio :on-close="hideAddPopup" />
        </woot-modal>

        <woot-delete-modal v-model:show="showDeleteConfirmationPopup" :on-close="closeDeletePopup"
            :on-confirm="confirmDeletion" :title="$t('CANNED_AUDIO_MGMT.DELETE.CONFIRM.TITLE')"
            :message="$t('CANNED_AUDIO_MGMT.DELETE.CONFIRM.MESSAGE')" :message-value="deleteMessage"
            :confirm-text="deleteConfirmText" :reject-text="deleteRejectText" />
    </SettingsLayout>
</template>
